<script setup lang="ts">
/**
 * Render-time error boundary.
 *
 * `AsyncBoundary` handles a request that failed. This handles a *component* that
 * threw — a bad prop, an undefined read, a screen that crashed during setup. Vue
 * unmounts the subtree in that case, so without this the user gets a blank page
 * with the navigation still around it, which reads as the app having quietly
 * broken rather than having failed.
 *
 * It resets on navigation: a crash on one screen must not follow the user to the
 * next one.
 */

import { onErrorCaptured, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import MpButton from '@/components/base/MpButton.vue'
import MpIcon from '@/components/base/MpIcon.vue'

const route = useRoute()
const caught = ref<Error | null>(null)

onErrorCaptured((error, _instance, info) => {
  caught.value = error instanceof Error ? error : new Error(String(error))
  // Structured, so it matches the backend's log shape (FND-08) and can be
  // shipped to CloudWatch RUM later without changing call sites.
  console.error(JSON.stringify({ level: 'error', event: 'render_error', screen: route.name, info, message: caught.value.message }))
  // Swallowed on purpose: the boundary is now rendering the failure, and letting
  // it propagate would take out the shell as well.
  return false
})

watch(() => route.fullPath, () => { caught.value = null })
</script>

<template>
  <div
    v-if="caught"
    class="mp-boundary"
    role="alert"
  >
    <span class="mp-boundary__mark"><MpIcon name="alert" /></span>
    <h2 class="mp-boundary__headline">
      This screen stopped working
    </h2>
    <p class="mp-boundary__body">
      Nothing you did caused it and nothing was saved or sent. The rest of the app still works — the
      navigation above will take you somewhere else, or reloading this screen may be enough.
    </p>
    <p class="mp-boundary__detail">
      <code>{{ caught.message }}</code>
    </p>
    <div class="mp-boundary__actions">
      <MpButton
        variant="primary"
        icon="refresh"
        @click="caught = null"
      >
        Reload this screen
      </MpButton>
      <MpButton
        variant="secondary"
        @click="$router.push('/home')"
      >
        Go to home
      </MpButton>
    </div>
  </div>
  <slot v-else />
</template>

<style scoped>
.mp-boundary {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  max-inline-size: 34rem;
  padding: var(--card-pad);
  background: var(--surface-raised);
  border: 1px solid var(--critical-text);
  border-radius: var(--radius-card);
}

.mp-boundary__mark {
  color: var(--critical-text);
}

.mp-boundary__headline {
  font-size: var(--font-size-h3);
}

.mp-boundary__body {
  color: var(--text-secondary);
  line-height: 1.6;
}

.mp-boundary__detail {
  font-size: var(--font-size-caption);
  color: var(--text-muted);
  overflow-wrap: anywhere;
}

.mp-boundary__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}
</style>
